import { performanceApi, type ClinicDrugsParams } from '../api/performance.api';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

/** 실적 쿼리 키 — 년월은 모두 정산월 yyyyMM */
export const performanceKeys = {
  all: ['performance'] as const,
  availableYears: () => [...performanceKeys.all, 'available-years'] as const,
  yearly: (year: number, clinicId?: number) =>
    [...performanceKeys.all, 'yearly', year, clinicId ?? null] as const,
  summary: (month: string, clinicId?: number) =>
    [...performanceKeys.all, 'summary', month, clinicId ?? null] as const,
  clinics: (month: string, clinicName: string) =>
    [...performanceKeys.all, 'clinics', month, clinicName] as const,
  trend: (month: string, clinicId: number) =>
    [...performanceKeys.all, 'trend', month, clinicId] as const,
  drugs: (
    month: string,
    clinicId: number,
    params: Omit<ClinicDrugsParams, 'page'>,
  ) => [...performanceKeys.all, 'drugs', month, clinicId, params] as const,
  pharmaCompanies: (month: string, clinicId: number) =>
    [...performanceKeys.all, 'pharma-companies', month, clinicId] as const,
  clinicImages: (month: string, clinicId: number) =>
    [...performanceKeys.all, 'clinic-images', month, clinicId] as const,
  imageDrugs: (month: string, clinicId: number, imageCode: string) =>
    [
      ...performanceKeys.all,
      'image-drugs',
      month,
      clinicId,
      imageCode,
    ] as const,
};

export const useAvailableYears = () =>
  useQuery({
    queryKey: performanceKeys.availableYears(),
    queryFn: performanceApi.availableYears,
  });

export const useYearlyPerformance = (
  year: number | undefined,
  clinicId?: number,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: performanceKeys.yearly(year ?? 0, clinicId),
    queryFn: () => performanceApi.yearly(year!, clinicId),
    enabled: !!year && (options?.enabled ?? true),
  });

/** 월 요약 — 데이터가 없는 달도 0으로 온다 */
export const useMonthlySummary = (month: string, clinicId?: number) =>
  useQuery({
    queryKey: performanceKeys.summary(month, clinicId),
    queryFn: () => performanceApi.monthlySummary(month, clinicId),
  });

export const CLINICS_PAGE_SIZE = 50;

/** 좌측 병의원 — 검색 · 정렬 · 페이징은 서버. 목록 끝이 보이면 다음 페이지 */
export const usePerformanceClinics = (month: string, clinicName: string) =>
  useInfiniteQuery({
    queryKey: performanceKeys.clinics(month, clinicName),
    queryFn: ({ pageParam }) =>
      performanceApi.clinics(month, {
        clinicName,
        page: pageParam,
        size: CLINICS_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.isLast ? undefined : last.pageNo + 1),
    placeholderData: keepPreviousData,
  });

/** 추이 차트 · 직전 5개월 평균 · 전월 대비 (차트는 선택 정산월의 연도) */
export const useClinicTrend = (month: string, clinicId?: number) =>
  useQuery({
    queryKey: performanceKeys.trend(month, clinicId ?? 0),
    queryFn: () =>
      performanceApi.clinicTrend(month, clinicId!, Number(month.slice(0, 4))),
    enabled: !!clinicId,
  });

export const DRUGS_PAGE_SIZE = 20;

/** 처방 카드 목록 — [더 보기]로 다음 페이지를 붙인다 */
export const useClinicDrugs = (
  month: string,
  clinicId: number | undefined,
  params: Omit<ClinicDrugsParams, 'page' | 'size'>,
  options?: { enabled?: boolean },
) => {
  const keyParams = { ...params, size: DRUGS_PAGE_SIZE };
  return useInfiniteQuery({
    queryKey: performanceKeys.drugs(month, clinicId ?? 0, keyParams),
    queryFn: ({ pageParam }) =>
      performanceApi.clinicDrugs(month, clinicId!, {
        ...keyParams,
        page: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.isLast ? undefined : last.pageNo + 1),
    placeholderData: keepPreviousData,
    enabled: !!clinicId && (options?.enabled ?? true),
  });
};

/** 제약사 칩 + 전체 합계 (전체 탭 제약사 ▾ 옵션도 여기서 만든다) */
export const useClinicPharmaCompanies = (month: string, clinicId?: number) =>
  useQuery({
    queryKey: performanceKeys.pharmaCompanies(month, clinicId ?? 0),
    queryFn: () => performanceApi.clinicPharmaCompanies(month, clinicId!),
    enabled: !!clinicId,
  });

export const useClinicImages = (month: string, clinicId?: number) =>
  useQuery({
    queryKey: performanceKeys.clinicImages(month, clinicId ?? 0),
    queryFn: () => performanceApi.clinicImages(month, clinicId!),
    enabled: !!clinicId,
  });

export const useImageDrugs = (
  month: string,
  clinicId: number,
  imageCode?: string,
) =>
  useQuery({
    queryKey: performanceKeys.imageDrugs(month, clinicId, imageCode ?? ''),
    queryFn: () => performanceApi.imageDrugs(month, clinicId, imageCode!),
    enabled: !!imageCode,
  });

/** M-09 엑셀 내보내기 — 누를 때마다 새 URL(5분)을 받는다. 재시도하지 않는다 */
export const useClinicExcelExport = () =>
  useMutation({
    mutationFn: ({ month, clinicId }: { month: string; clinicId: number }) =>
      performanceApi.clinicExcel(month, clinicId),
    retry: 0,
  });
